import { useCallback } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { M365ApprovalStatus } from "@/types/user-settings"

import { useUISettings } from "@/hooks/use-module-settings"

import { CURRENT_M365_APPROVAL_VERSION, resolveM365ApprovalStatus } from "../m365-approval"

export { CURRENT_M365_APPROVAL_VERSION }

export function useM365ApprovalGate() {
  const { settings, updateSettings, isLoading, isFetching } = useUISettings()
  const queryClient = useQueryClient()

  // Treat the gate as "still loading" until react-query has stable, hydrated
  // data — not just "first-fetch finished." `isLoading` only covers the very
  // first fetch when there's no cached data; on a returning visit (inside the
  // 10-minute gcTime window) react-query serves stale cache while a background
  // refetch is in flight, and `isLoading` is already false. If the consent
  // dialog reads the gate during that window and the cache happens to be from
  // before the user approved (other tab, expired snapshot), it briefly thinks
  // the user hasn't decided yet and pops. Including `isFetching` and the
  // `settings === undefined` guard mirrors the central onboarding gate pattern
  // (see compliance-dialog.tsx) so the popup waits for confirmed data instead
  // of guessing from a transient empty state.
  const isM365ApprovalLoading = isLoading || isFetching || settings === undefined
  // Narrower gate for SENDING a message (#3323 E10): true only while no
  // approval data has arrived at all. A background refetch (a new settings
  // observer mounting after New chat once the cached settings are older than
  // their stale time) keeps serving the confirmed data, so it must not block a
  // send — it made the first message after New chat fail with "still loading".
  // The consent dialog keeps the stricter `isM365ApprovalLoading` above.
  const isM365ApprovalUnresolved = isLoading || settings === undefined

  // A decision only counts if it was made against the current consent version.
  // Bumping CURRENT_M365_APPROVAL_VERSION (e.g. when new data sources are added
  // to the consent scope) re-prompts both approved and declined users. The
  // onboarding resolver reads the same helper so the wizard step and this gate
  // can never disagree about whether the user has decided.
  const status = resolveM365ApprovalStatus(settings)

  const hasM365Approval = status === "approved"
  const hasDeclinedM365 = status === "declined"

  // Drop the cached home_context entirely on consent changes — `removeQueries`,
  // not `invalidateQueries`. Invalidation only marks stale; the cached Graph
  // payload keeps serving any consumer that reads it until a refetch lands,
  // which means surfaces that read home_context unconditionally (about-me,
  // what-we-know) can briefly serve stale Graph data right after a decline.
  // removeQueries clears the cache outright; the next active observer starts
  // from scratch (a re-approved user, or a remount with the query enabled).
  //
  // Must run inside `updateSettings`'s `onSuccess` — not synchronously after
  // the call. `updateSettings` is a react-query mutation; clearing before the
  // mutation lands server-side would let the next fetch race against the OLD
  // approval state (e.g. on decline, the backend still reads "approved" in
  // user settings and the cache would repopulate with fresh Graph data).
  // Waiting for `onSuccess` guarantees subsequent reads see the updated
  // approval status.
  const clearHomeContext = useCallback(() => {
    queryClient.removeQueries({ queryKey: ["home-context"] })
  }, [queryClient])

  // Persist a decision at the current consent version. Resolves once the
  // settings write has landed (and the Graph cache is cleared), so callers that
  // need to sequence a follow-up write — the onboarding wizard persists its
  // next step after the decision — can await it. Rejects on write failure.
  const decideM365 = useCallback(
    (status: M365ApprovalStatus) =>
      new Promise<void>((resolve, reject) => {
        updateSettings(
          {
            m365_approval_version: CURRENT_M365_APPROVAL_VERSION,
            m365_approval_timestamp: new Date().toISOString(),
            m365_approval_status: status,
          },
          {
            onSuccess: () => {
              clearHomeContext()
              resolve()
            },
            onError: () => reject(new Error("Failed to save Microsoft 365 decision")),
          }
        )
      }),
    [updateSettings, clearHomeContext]
  )

  // Fire-and-forget variants for the standalone dialog and the Preferences
  // toggle, which already surface write failures through the settings hook.
  const approveM365 = useCallback(() => {
    decideM365("approved").catch(() => undefined)
  }, [decideM365])

  const declineM365 = useCallback(() => {
    decideM365("declined").catch(() => undefined)
  }, [decideM365])

  return {
    approveM365,
    declineM365,
    decideM365,
    hasM365Approval,
    hasDeclinedM365,
    status,
    isM365ApprovalLoading,
    isM365ApprovalUnresolved,
    approvalTimestamp: settings?.m365_approval_timestamp,
  }
}
